import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ArrowRightLeft, Plus, Trash2, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { groupName } from "@/lib/leadGroups";

const KEEP_RAW = "__keep_raw__";

/** Settings → Call Log Import → Outcome Mapping.
 *  Maps Genesys result / wrap-up codes to Kippu dispositions. This is a
 *  reporting suggestion only: the raw Genesys outcome is always kept on the
 *  call-history entry and an import never sets a lead's disposition. */
export default function OutcomeMappingPanel() {
  const { activeSlug } = useClient();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ genesys_code: "", disposition: KEEP_RAW });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/lead-outcome-mappings");
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load outcome mapping");
    }
  }, []);

  useEffect(() => { load(); }, [load, activeSlug]);

  const save = async (payload, quiet = false) => {
    setBusy(true);
    try {
      await api.post("/lead-outcome-mappings", payload);
      if (!quiet) toast.success("Mapping saved");
      await load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    const code = (form.genesys_code || "").trim();
    if (!code) return toast.error("Enter the Genesys result or wrap-up code");
    const ok = await save({
      genesys_code: code,
      disposition: form.disposition === KEEP_RAW ? null : form.disposition,
      active: true,
    });
    if (ok) setForm({ genesys_code: "", disposition: KEEP_RAW });
  };

  const remove = async (row) => {
    setBusy(true);
    try {
      await api.delete("/lead-outcome-mappings", { params: { genesys_code: row.genesys_code } });
      toast.success(`Removed — future imports keep "${row.genesys_code}" as-is`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Restore the shipped mapping for the default Genesys codes? Codes you added yourself are kept.")) return;
    setBusy(true);
    try {
      const res = await api.post("/lead-outcome-mappings/reset-defaults");
      toast.success(`Restored ${res.data.restored} default mapping(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <div className="text-xs text-slate-400 p-4" data-testid="outcome-mapping-loading">Loading outcome mapping…</div>;
  }

  const canManage = data.can_manage;
  const dispositions = data.dispositions || [];
  const dispLabel = (v) => {
    const d = dispositions.find((x) => x.value === v);
    return d ? `${d.label} (${d.value})` : v;
  };

  return (
    <Card data-testid="outcome-mapping-panel">
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-sm flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4 text-indigo-600" /> Outcome Mapping
          </CardTitle>
          <p className="text-[11px] text-slate-500 mt-1 max-w-2xl">
            How each Genesys result or wrap-up code is interpreted in Kippu reporting. The raw
            Genesys outcome is always stored and displayed as-is — this mapping never changes a
            lead&apos;s disposition or its owner. Codes with no mapping simply keep their raw text.
          </p>
        </div>
        {canManage && (
          <Button variant="outline" size="sm" className="h-7 text-[10px] shrink-0"
                  disabled={busy} onClick={resetDefaults} data-testid="outcome-reset-btn">
            <RotateCcw className="w-3 h-3 mr-1" /> Restore defaults
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Codes seen in imports that nothing maps yet */}
        {(data.unmapped || []).length > 0 && (
          <div className="border border-amber-200 bg-amber-50 rounded p-2 space-y-1.5"
               data-testid="outcome-unmapped">
            <div className="text-[11px] text-amber-900">
              <strong>{data.unmapped.length}</strong> Genesys code(s) appear in imported call history
              with no mapping. They display as their raw code — map them if you want them grouped in reporting.
            </div>
            <div className="flex flex-wrap gap-1.5">
              {data.unmapped.map((u) => (
                <button
                  key={u.genesys_code} type="button" disabled={!canManage}
                  onClick={() => setForm({ genesys_code: u.genesys_code, disposition: KEEP_RAW })}
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-amber-300 bg-white hover:border-amber-500 disabled:opacity-60"
                  data-testid={`outcome-unmapped-${u.genesys_code}`}
                >
                  {u.genesys_code} · {u.attempts}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Add / edit row */}
        {canManage && (
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                Genesys result / wrap-up code
              </Label>
              <Input
                value={form.genesys_code}
                onChange={(e) => setForm((f) => ({ ...f, genesys_code: e.target.value }))}
                placeholder="e.g. ININ-OUTBOUND-NO-ANSWER"
                className="h-8 text-xs mt-1 font-mono"
                data-testid="outcome-code-input"
              />
            </div>
            <div className="w-[230px]">
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                Interpret as
              </Label>
              <Select value={form.disposition}
                      onValueChange={(v) => setForm((f) => ({ ...f, disposition: v }))}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="outcome-disposition-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value={KEEP_RAW} className="text-xs">
                    — Keep raw outcome (no mapping) —
                  </SelectItem>
                  {dispositions.map((d) => (
                    <SelectItem key={d.value} value={d.value} className="text-xs">
                      {d.label} ({d.value}){d.ownership_group ? ` · ${groupName(d.ownership_group)}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={add} disabled={busy || !form.genesys_code.trim()}
                    className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="outcome-add-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    : <Plus className="w-3.5 h-3.5 mr-1.5" />}
              Save mapping
            </Button>
          </div>
        )}

        {/* Existing mappings */}
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-xs" data-testid="outcome-mapping-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left px-3 py-1.5">Genesys code</th>
                <th className="text-left px-2 py-1.5">Interpreted as</th>
                <th className="text-left px-2 py-1.5">Reporting label</th>
                <th className="text-left px-2 py-1.5">Source</th>
                <th className="text-center px-2 py-1.5">Active</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {(data.items || []).length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                  No mappings — every Genesys code will display exactly as received.
                </td></tr>
              )}
              {(data.items || []).map((row) => (
                <tr key={row.genesys_code} className="border-t border-slate-100"
                    data-testid={`outcome-row-${row.genesys_code}`}>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-slate-700">{row.genesys_code}</td>
                  <td className="px-2 py-1.5">
                    {canManage ? (
                      <Select
                        value={row.disposition || KEEP_RAW}
                        onValueChange={(v) => save({
                          genesys_code: row.genesys_code,
                          disposition: v === KEEP_RAW ? null : v,
                          active: row.active !== false,
                        })}
                      >
                        <SelectTrigger className="h-6 text-[10px] w-[200px]"
                                       data-testid={`outcome-select-${row.genesys_code}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value={KEEP_RAW} className="text-xs">— Keep raw outcome —</SelectItem>
                          {dispositions.map((d) => (
                            <SelectItem key={d.value} value={d.value} className="text-xs">
                              {d.label} ({d.value})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-slate-600">
                        {row.disposition ? dispLabel(row.disposition) : "— keep raw —"}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600">{row.label || "—"}</td>
                  <td className="px-2 py-1.5">
                    <Badge variant="outline" className="text-[9px]">
                      {row.is_default ? "default" : "custom"}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <Switch
                      checked={row.active !== false}
                      disabled={!canManage}
                      onCheckedChange={(v) => save({
                        genesys_code: row.genesys_code,
                        disposition: row.disposition,
                        active: v,
                      }, true)}
                      className="scale-75"
                      data-testid={`outcome-active-${row.genesys_code}`}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {canManage && (
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-rose-500"
                              onClick={() => remove(row)}
                              data-testid={`outcome-delete-${row.genesys_code}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
